import type { ErrorInfo } from '../../ui/utils/error-handling';

type KnownAnalyticSourceIds = 'confluenceTitleToolbar' | string;

export type Unsubscribe = () => void;

export type ConfigType = 'rangeConfig' | 'emptyConfig';

const EventHubEvent = {
	StartPrompt: 'start prompt',
	ReplaceConfluenceTitle: 'replace title in confluence',
	AiExperienceLlmComplete: 'ai experience llm complete',
} as const;

export type EventHubEventUnionType = (typeof EventHubEvent)[keyof typeof EventHubEvent];

type BaseData = {
	analyticSourceId: KnownAnalyticSourceIds;
};

type StreamData =
	| { type: 'initialising' }
	| { type: 'analysingQuery' }
	| {
			result: string;
			type: 'streaming';
	  }
	| { result: string; type: 'complete' }
	| { errorInfo: ErrorInfo; type: 'error' }
	| { type: 'response too similar' };

export type StreamingCallback = (result: StreamData) => void;

export type AdjustSelectionInstruction = 'fullDocument' | 'keepCurrent';

export type AdditionalPayloadDataForIssueReformatter = {
	additionalInfo?: string;
	context?: {
		issue_key: string;
		project_id: string;
	};
	feature: string;
	linked_urls: string[];
};

const LINK_SOURCE_TYPE = {
	CONFLUENCE: 'CONFLUENCE_LINK',
	LOOM: 'LOOM_LINK',
	GOOGLE_DOC: 'GOOGLE_DOC_LINK',
} as const;
export type DescriptionCompleterAdditionalPayload = {
	context: {
		primary_link: string;
	};
	source: (typeof LINK_SOURCE_TYPE)[keyof typeof LINK_SOURCE_TYPE];
};

export type AdditionalPayloadDataForDraftReplyPill = {
	category: string;
	commentType: string;
	issueId: string;
	pills?: Array<{
		category: string;
		example?: string;
		goal?: string;
		intent?: string;
		text: string;
		triggerSummary?: string;
	}>;
};

export type IncidentSmartCommentsAdditionalPayload = {
	isExternal?: boolean;
	replyIntent: string;
};

export type SuggestCommentCommentType =
	| 'QUICK_REPLY'
	| 'QUESTION'
	| 'SUGGESTION'
	| 'JIRA_STATUS_UPDATE'
	| 'JIRA_COMMENT_REPLY'
	| 'JIRA_ASSIGNEE_MISSING'
	| 'JIRA_DESCRIPTION_MISSING'
	| 'JIRA_MOTIVATION';

export type SuggestCommentAdditionalPayload = {
	commentType: SuggestCommentCommentType;
};

type ToEditorModalModes = {
	hideModal?: boolean;
	streamingCallback?: StreamingCallback;
} & (
	| {
			hideModal?: false;
	  }
	| {
			hideModal: true;
			streamingCallback: StreamingCallback;
	  }
);

type ToEditorBaseData = BaseData & {
	adjustSelection?: AdjustSelectionInstruction;
	targetEditorId: string | 'global';
} & ToEditorModalModes;

type FromEditorBaseData = BaseData & {
	sourceEditorId: string;
};

export const EventHubPrompt = {
	SuggestTitleConfluenceTitleToolbar: 'suggest title in confluence title toolbar',
	SuggestTitle: 'suggest title',
	DraftReply: 'draft reply',
	GenerateEscalationNotes: 'generate escalation notes',
	IncidentSmartComments: 'incident smart comments',
	CustomPrompt: 'custom prompt',
	IssueReformatter: 'issue reformatter',
	DescriptionCompleter: 'description completer',
	Summarize: 'summarize',
	ImproveWriting: 'improve writing',
	MakeShorter: 'make shorter',
	FixSpellingGrammar: 'fix spelling and grammar',
	ChangeToneToProfessional: 'change tone to professional',
	ChangeToneToNeutral: 'change tone to neutral',
	ChangeToneToEmpathetic: 'change tone to empathetic',
	ChangeToneToCasual: 'change tone to casual',
	ChangeToneToEducational: 'change tone to educational',
	DraftReplyPill: 'draft reply pill',
	SuggestComment: 'suggest comment',
} as const;

type BaseStartPromptData = ToEditorBaseData;

export type StartPromptData = BaseStartPromptData &
	(
		| {
				prompt: (typeof EventHubPrompt)['SuggestTitleConfluenceTitleToolbar'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['SuggestTitle'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['CustomPrompt'];
				promptText: string;
		  }
		| {
				additionalPayloadDataIssueReformatter?: AdditionalPayloadDataForIssueReformatter;
				// this will always currently use the rangeConfig (i.e. fullDocument)
				// selectioning behaviour, so should not be set by consumers
				adjustSelection?: never;
				prompt: (typeof EventHubPrompt)['IssueReformatter'];
		  }
		| {
				additionalPayloadData?: DescriptionCompleterAdditionalPayload;
				// this will always currently use the rangeConfig (i.e. fullDocument)
				// selectioning behaviour, so should not be set by consumers
				adjustSelection?: never;
				prompt: (typeof EventHubPrompt)['DescriptionCompleter'];
		  }
		| {
				additionalPayloadData?: AdditionalPayloadDataForDraftReplyPill;
				adjustSelection?: never;
				prompt: (typeof EventHubPrompt)['DraftReplyPill'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['Summarize'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ImproveWriting'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['MakeShorter'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['FixSpellingGrammar'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ChangeToneToProfessional'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ChangeToneToNeutral'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ChangeToneToEmpathetic'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ChangeToneToCasual'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['ChangeToneToEducational'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['DraftReply'];
		  }
		| {
				prompt: (typeof EventHubPrompt)['GenerateEscalationNotes'];
		  }
		| {
				additionalPayloadData?: IncidentSmartCommentsAdditionalPayload;
				adjustSelection?: never;
				prompt: (typeof EventHubPrompt)['IncidentSmartComments'];
		  }
		| {
				additionalPayloadData?: SuggestCommentAdditionalPayload;
				adjustSelection?: never;
				prompt: (typeof EventHubPrompt)['SuggestComment'];
		  }
	);

export type ReplaceConfluenceTitleData = FromEditorBaseData & {
	fireUnifiedAnalyticsEvent?: (eventType: 'actioned' | 'discarded') => void;
	// On cleaning "platform_editor_ai_suggest_title_actioned_event" fg, make below two options compulsory.
	isFinal?: boolean;
	title: string;
};

/**
 * Published when an AI experience stream completes (insert/accept/auto-accept).
 * Products can subscribe to handle product-specific logic (e.g. draft reply evaluation).
 */
export type AiExperienceLlmCompleteData = FromEditorBaseData & {
	configItemKey: string;
	draftReplyMetadata?: AdditionalPayloadDataForDraftReplyPill;
	markdown: string;
};

export type ToEditorEventDataMap = {
	[EventHubEvent.StartPrompt]: StartPromptData;
};

export type FromEditorEventDataMap = {
	[EventHubEvent.AiExperienceLlmComplete]: AiExperienceLlmCompleteData;
	[EventHubEvent.ReplaceConfluenceTitle]: ReplaceConfluenceTitleData;
};

export type EventDataMap = ToEditorEventDataMap & FromEditorEventDataMap;

export type SubscribeParams<T extends keyof EventDataMap> = {
	callback: (data: EventDataMap[T]) => void;
	event: T;
};
